/**
 * Module-level constants for the data layer.
 *
 * Keeping these in one place makes it obvious which values are
 * configuration vs. derived state, and avoids accidental import cycles
 * when other modules in this folder need to reach for them.
 */

import settings from '$lib/config/settings.json';

/** How often the long-poller refreshes match data, in milliseconds. */
export const POLL_INTERVAL = 15000;

/** True when running the special preview build (graphics.thomsonreuters.com). */
export const IS_PREVIEW_BUILD = import.meta.env.PREVIEW === true;

/**
 * Empty/safe shape returned when remote/local data isn't available yet
 * (e.g. pre-tournament when only tournament-calendar.json has been published).
 * Keeps downstream code that does `optaData.matches.matches.match` from crashing.
 */
export const EMPTY_DATA = {
  matches: { matches: { match: [] } },
  teamStats: null,
  topPlayers: null,
  topTeams: null,
  topPlayerStats: null,
};

/**
 * Tournament identifiers + production URL parts, sourced from
 * `src/lib/config/settings.json` (see `src/lib/config/README.md`). The
 * data-toolkit CLI keeps this file in sync with `.env` (see
 * `pnpm toolkit --command=setup-local-data`).
 *
 * Re-exported as named constants for backwards compatibility with the
 * rest of the data layer.
 */
export const CURRENT_TOURNAMENT_YEAR = settings.tournament.current.year;
export const CURRENT_TOURNAMENT_ID = settings.tournament.current.id;
export const PREVIOUS_TOURNAMENT_YEAR = settings.tournament.previous.year;
export const PREVIOUS_TOURNAMENT_ID = settings.tournament.previous.id;

/** Tournament ID exported for backwards compatibility with consumers. */
export const TOURNAMENT_ID = CURRENT_TOURNAMENT_ID;

/**
 * S3 base URL — the fully-resolved live data endpoint.
 *
 * Sourced directly from `production.dataUrl` in
 * `src/lib/config/settings.json`, which is written by:
 *
 *   pnpm toolkit --command=setup-live-endpoint
 *
 * The toolkit assembles it as `${baseUrl}/${year}/${pathPrefix}/${id}`,
 * e.g. `https://graphics.thomsonreuters.com/data/2026/opta/soccer/873cbl9cd9butm4air0mugxzo`.
 *
 * If `production.dataUrl` is missing (e.g. on a checkout where
 * `setup-live-endpoint` hasn't been run yet), we fall back to assembling
 * the URL from the same parts at runtime so the app keeps working.
 */
export const S3_URL =
  settings.production.dataUrl ||
  `${settings.production.baseUrl}/${CURRENT_TOURNAMENT_YEAR}` +
    `/${settings.production.pathPrefix}/${CURRENT_TOURNAMENT_ID}`;
